<template>
  <div
    class="rechercheDetailMainWrapper"
    ref="rechercheDetailMainWrapper"
  >
    <div class="navigationWrapper">
      <BasicButton
        type="reverse"
        iconName="arrow-left"
        label="Retour à la liste"
        @click="useFactureRoutingService().navigateToRechercheFacturesList"
      />
    </div>
    <div class="topWrapper">
      <BasicCard
        class="cardDesktop"
        :title="(store.model?.type === 'Facture' ? 'Facture n° ' : 'Facture Proforma groupée n° ') + `${store.model?.reference}`"
        :subtitles="[
          {
            text:
              'Emetteur : ' +
              store.model?.etablissementEmetteur?.denomination +
              ' ' +
              store.model?.etablissementEmetteur.siret,
          },
        ]"
        :badge="{
          label: StatutFactureLibelle[store.model?.statut as StatutFactureDTO],
          type: StatutFactureBadgeType[store.model?.statut as StatutFactureDTO],
        }"
      />
      <BasicCard
        class="cardMobile"
        :title="'Facture n° ' + `${store.model?.reference}`"
        :subtitles="[
          {
            text:
              'Emetteur : ' +
              store.model?.etablissementEmetteur?.denomination +
              ' ' +
              store.model?.etablissementEmetteur.siret,
          },
        ]"
        :badge="{
          label: StatutFactureLibelle[store.model?.statut as StatutFactureDTO],
          type: StatutFactureBadgeType[store.model?.statut as StatutFactureDTO],
        }"
      />
      <BasicCard
        v-if="store.model?.statut != 'Rejete'"
        title="Informations bancaires"
        :subtitles="[
          { text: 'IBAN : ' + (store.model?.rib?.iban ? store.model?.rib?.iban : '-'), icon: 'bank' },
          { text: 'BIC : ' + (store.model?.rib?.bic ? store.model?.rib?.bic : '-'), icon: 'bank' },
          {
            text:
              'Payé par : ' + (store.model?.modeReglement ? ModeReglementLibelle[store.model?.modeReglement!] : '-'),
            icon: 'wallet',
          },
          { text: 'Payé le : ' + dateTimeToReadableDate(store.model?.virements[0]?.dateTraitement), icon: 'calendar' },
        ]"
      />
    </div>
    <div class="contentWrapper">
      <div class="doubleSection">
        <div class="sectionWrapper sectionWrapper--financements">
          <div class="financementsTitleWrapper">
            <span class="sectionTitle">Type : {{ store.model?.typePiece }}</span>
          </div>
          <div class="financementsGrid">
            <div class="financementsGridElem">
              <span class="financementsTitle">Référence</span>
              <span class="financementsSubtitle">{{ store.model?.reference }}</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Identifiant</span>
              <span class="financementsSubtitle">{{ store.model?.idSor }}</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Date d'émission</span>
              <span class="financementsSubtitle">{{ dateTimeToReadableDate(store.model?.dateEmission) }}</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Date de réception</span>
              <span class="financementsSubtitle">{{ dateTimeToReadableDate(store.model?.dateReception) }}</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Montant HT</span>
              <span class="financementsSubtitle">
                {{ formatNumber(store.model?.montantHt, { decimal: 2, separateur: true }) }} €
              </span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Montant TTC</span>
              <span class="financementsSubtitle">
                {{ formatNumber(store.model?.montantTTC, { decimal: 2, separateur: true }) }} €
              </span>
            </div>
            <div class="financementsGridElem" v-if="store.model?.type !== 'Proforma'">
              <span class="financementsTitle">Montant Avoir HT</span>
              <span class="financementsSubtitle">
                {{ formatNumber(store.model?.montantAvoirHt, { decimal: 2, separateur: true }) }} €
              </span>
            </div>
            <div class="financementsGridElem" v-if="store.model?.type !== 'Proforma'">
              <span class="financementsTitle">Montant Avoir TTC</span>
              <span class="financementsSubtitle">
                {{ formatNumber(store.model?.montantAvoirTTC, { decimal: 2, separateur: true }) }} €
              </span>
            </div>
            <div
              v-if="store.model?.type === 'Proforma'"
              class="financementsGridElem"
            >
              <span class="financementsTitle">Type de public</span>
              <span class="financementsSubtitle">
                {{ store.model?.remboursements[0]?.typePublic ?? '-' }}
              </span>
            </div>
            <div
              v-if="store.model?.type === 'Proforma'"
              class="financementsGridElem"
            >
              <span class="financementsTitle">Financeur</span>
              <span class="financementsSubtitle">
                {{ store.model?.remboursements[0]?.financeur ?? '-' }}
              </span>
            </div>
          </div>
        </div>
        <DossiersList
          v-if="store.model?.type === 'Facture'"
          :dossiers="store.model?.dossiers!"
        />
        <DRList
          v-if="store.model?.type === 'Proforma'"
          :remboursements="store.model?.remboursements!"
        />
        <DocumentsList
          :code="store.model?.code!"
          :dispositif="[...Dispositif]"
          endpoint="factures"
          :pieces="store.model?.pieces!"
          :statut="store.model?.statut!"
          :natures-authorisees="store.model?.naturesPiecesDeposables"
        />
        <CourriersList
          :courriers="store.model?.courriers!"
          :code="store.model?.code!"
          endpoint="factures"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Dispositif } from '@/features/domaine/types/opco'
  import { useFactureStore } from './useFactureStore'
  import { useFactureRoutingService } from './useFactureRoutingService'
  import DossiersList from './components/DossiersList.vue'
  import DRList from './components/DRList.vue'
  import CourriersList from '../components/CourriersList.vue'
  import { dateTimeToReadableDate, formatNumber } from '@/features/shared/tools'
  import {
    type StatutFactureDTO,
    StatutFactureLibelle,
    StatutFactureBadgeType,
  } from '@/features/api/extranet/dto/Facture/StatutFactureDTO'
  import DocumentsList from '../components/DocumentsList.vue'
  import { ModeReglementLibelle } from '@/features/api/extranet/dto/Facture/ModeReglementDTO'
  import { onBeforeUnmount } from 'vue'

  const store = useFactureStore()
  onBeforeUnmount(store.$reset)
</script>

<style lang="less" scoped>
  @import '../index.less';
</style>
